import dayjs from 'dayjs';
import { TableCell } from 'components/common';
import { IProductDataTransformed } from 'queries';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { GENERAL_DATE_TIME_FORMAT } from 'variables';
import { formattedPercentConfig, ITableCell } from './types';
import { ProductTypeEnum } from '../../../../legacy/shared-types';

const formatDate = (date?: Date | null) => {
  return date ? dayjs(date).format(GENERAL_DATE_TIME_FORMAT) : '';
};

export const rowDataFormatter = (data: IProductDataTransformed, index: number) => {
  const headersCells: ITableCell[] = [
    { cell: <TableCell key={`description-${index}`}>{data.description}</TableCell> },
    { cell: <TableCell key={`productCategory-${index}`}>{data.productCategory}</TableCell> },
    { cell: <TableCell key={`productRange-${index}`}>{data.productRange}</TableCell> },
    { cell: <TableCell key={`productType-${index}`}>{data.productType}</TableCell> },
    { cell: <TableCell key={`monthsInitial-${index}`}>{data.monthsInitial.toString()}</TableCell> },
    {
      cell: (
        <TableCell key={`maximumFTV-${index}`}>
          <FormattedNumber value={data.maximumFTV} {...formattedPercentConfig} />
        </TableCell>
      ),
    },
    {
      cell: (
        <TableCell key={`initialRate-${index}`}>
          <FormattedNumber value={data.initialRate} {...formattedPercentConfig} />
        </TableCell>
      ),
    },
    { cell: <TableCell key={`productFees-${index}`}>{data.productFees.toString()}</TableCell> },
    { cell: <TableCell key={`productFeesPercentage-${index}`}>{data.productFeesPercentage.toString()}</TableCell> },
    {
      cell: (
        <TableCell key={`equityShare-${index}`}>
          <FormattedNumber value={data.equityShare} {...formattedPercentConfig} />
        </TableCell>
      ),
    },
  ];

  const bodyCells: ITableCell[] = [
    {
      cell: (
        <div key={`product.code.label-${index}`}>
          <h4 className={'m-1'} data-testid={'productBodyCell'}>
            <FormattedMessage id={'product.code.label'} />
          </h4>
          <p className={'m-1'}>{data.code}</p>
        </div>
      ),
    },
    {
      cell: (
        <div key={`product.initialBaseRate.label-${index}`}>
          <h4 className={'m-1'} data-testid={'productBodyCell'}>
            <FormattedMessage id={'product.initialBaseRate.label'} />
          </h4>
          <p className={'m-1'}>
            <FormattedNumber value={data.initialBaseRate} {...formattedPercentConfig} />
            {`${data.productType !== ProductTypeEnum.DISCOUNT ? '' : ' (BoE)'}`}
          </p>
        </div>
      ),
    },
    {
      cell: (
        <div key={`product.initialMargin.label-${index}`}>
          <h4 className={'m-1'} data-testid={'productBodyCell'}>
            <FormattedMessage id={'product.initialMargin.label'} />
          </h4>
          <p className={'m-1'}>
            <FormattedNumber value={data.initialMargin} {...formattedPercentConfig} />
          </p>
        </div>
      ),
    },
    {
      cell: (
        <div key={`product.closeDate.label-${index}`}>
          <h4 className={'m-1'} data-testid={'productBodyCell'}>
            <FormattedMessage id={'product.closeDate.label'} />
          </h4>
          <p className={'m-1'}>{formatDate(data.closeDate)}</p>
        </div>
      ),
    },
    {
      cell: (
        <div key={`product.launchDate.label-${index}`}>
          <h4 className={'m-1'} data-testid={'productBodyCell'}>
            <FormattedMessage id={'product.launchDate.label'} />
          </h4>
          <p className={'m-1'}>{formatDate(data.launchDate)}</p>
        </div>
      ),
    },
    {
      cell: (
        <div key={`product.margin.label-${index}`}>
          <h4 className={'m-1'} data-testid={'productBodyCell'}>
            <FormattedMessage id={'product.margin.label'} />
          </h4>
          <p className={'m-1'}>
            <FormattedNumber value={data.margin} {...formattedPercentConfig} />
          </p>
        </div>
      ),
    },
    {
      cell: (
        <div key={`product.productTier.label-${index}`}>
          <h4 className={'m-1'} data-testid={'productBodyCell'}>
            <FormattedMessage id={'product.productTier.label'} />
          </h4>
          <p className={'m-1'}>{data.productTier}</p>
        </div>
      ),
    },
    {
      cell: (
        <div key={`product.revertRate.label-${index}`}>
          <h4 className={'m-1'} data-testid={'productBodyCell'}>
            <FormattedMessage id={'product.revertRate.label'} />
          </h4>
          <p className={'m-1'}>
            <FormattedNumber value={data.revertRate} {...formattedPercentConfig} />
          </p>
        </div>
      ),
    },
    {
      cell: (
        <div key={`product.createdBy.label-${index}`}>
          <h4 className={'m-1'} data-testid={'productBodyCell'}>
            <FormattedMessage id={'product.createdBy.label'} />
          </h4>
          <p className={'m-1'}>{data.creator ? `${data.creator.firstName} ${data.creator.lastName}` : '-'}</p>
        </div>
      ),
    },
    {
      cell: (
        <div key={`product.createdAt.label-${index}`}>
          <h4 className={'m-1'} data-testid={'productBodyCell'}>
            <FormattedMessage id={'product.createdAt.label'} />
          </h4>
          <p className={'m-1'}>{formatDate(data.createdAt)}</p>
        </div>
      ),
    },
    {
      cell: (
        <div key={`product.verifiedBy.label-${index}`}>
          <h4 className={'m-1'} data-testid={'productBodyCell'}>
            <FormattedMessage id={'product.verifiedBy.label'} />
          </h4>
          <p className={'m-1'}>{data.verifier ? `${data.verifier.firstName} ${data.verifier.lastName}` : '-'}</p>
        </div>
      ),
    },
    {
      cell: (
        <div key={`product.verifiedAt.label-${index}`}>
          <h4 className={'m-1'} data-testid={'productBodyCell'}>
            <FormattedMessage id={'product.verifiedAt.label'} />
          </h4>
          <p className={'m-1'}>{formatDate(data.verifiedAt) || '-'}</p>
        </div>
      ),
    },
  ];

  return { headersCells, bodyCells };
};
